import { contactsEntity } from "./entities/contacts";
import { companiesEntity } from "./entities/companies";
import { dealsEntity } from "./entities/deals";
import { ticketsEntity } from "./entities/tickets";
import { timeEntriesEntity } from "./entities/time-entries";
import { tasksEntity } from "./entities/tasks";
import { communicationsEntity } from "./entities/communications";
import { recurringTransactionsEntity } from "./entities/recurring-transactions";
import { recurringChargesEntity } from "./entities/recurring-charges";
import { contractsEntity } from "./entities/contracts";
import { socialMediaPostsEntity } from "./entities/social-media-posts";
import { emailCampaignsEntity } from "./entities/email-campaigns";
import { employeesEntity } from "./entities/employees";
import { adminUsersEntity } from "./entities/admin-users";
import { offeringsEntity } from "./entities/offerings";
import type { EntityDefinition } from "./types";
import type { DetailPageConfig } from "@/components/shared/UniversalDetailPage/types/detailPage";
import type { SimpleTableConfig } from "@/components/shared/UniversalCRMTable/types";
import type { BaseEntity } from "@/lib/types/table/shared";
import { ENTITY_REGISTRY_KEYS, isEntityRegistryKey } from "./keys";
import type { EntityRegistryKey } from "./keys";

export { ENTITY_REGISTRY_KEYS, isEntityRegistryKey };
export type { EntityRegistryKey };

type EntityRegistryMap = Record<EntityRegistryKey, EntityDefinition<BaseEntity>>;

function buildEntityRegistry(): EntityRegistryMap {
  return {
    contacts: contactsEntity as unknown as EntityDefinition<BaseEntity>,
    companies: companiesEntity as unknown as EntityDefinition<BaseEntity>,
    deals: dealsEntity as unknown as EntityDefinition<BaseEntity>,
    tickets: ticketsEntity as unknown as EntityDefinition<BaseEntity>,
    time_entries: timeEntriesEntity as unknown as EntityDefinition<BaseEntity>,
    tasks: tasksEntity as unknown as EntityDefinition<BaseEntity>,
    communications: communicationsEntity as unknown as EntityDefinition<BaseEntity>,
    recurring_transactions: recurringTransactionsEntity as unknown as EntityDefinition<BaseEntity>,
    recurring_charges: recurringChargesEntity as unknown as EntityDefinition<BaseEntity>,
    contracts: contractsEntity as unknown as EntityDefinition<BaseEntity>,
    social_media_posts: socialMediaPostsEntity as unknown as EntityDefinition<BaseEntity>,
    email_campaigns: emailCampaignsEntity as unknown as EntityDefinition<BaseEntity>,
    employees: employeesEntity as unknown as EntityDefinition<BaseEntity>,
    admin_users: adminUsersEntity as unknown as EntityDefinition<BaseEntity>,
    offerings: offeringsEntity as unknown as EntityDefinition<BaseEntity>,
  };
}

/** Object holder avoids TDZ when getters run during circular module init. */
const registryCache: { map: EntityRegistryMap | undefined } = { map: undefined };

function getEntityRegistry(): EntityRegistryMap {
  if (!registryCache.map) {
    registryCache.map = buildEntityRegistry();
  }
  return registryCache.map;
}

/** Lazy proxy so importing the barrel during init does not hit TDZ on the registry object. */
export const ENTITY_REGISTRY = new Proxy({} as EntityRegistryMap, {
  get(_target, prop) {
    if (typeof prop !== "string") return undefined;
    return getEntityRegistry()[prop as EntityRegistryKey];
  },
  has(_target, prop) {
    if (typeof prop !== "string") return false;
    return prop in getEntityRegistry();
  },
  ownKeys() {
    return [...ENTITY_REGISTRY_KEYS];
  },
  getOwnPropertyDescriptor(_target, prop) {
    if (typeof prop !== "string") return undefined;
    const registry = getEntityRegistry();
    if (!(prop in registry)) return undefined;
    return {
      enumerable: true,
      configurable: true,
      value: registry[prop as EntityRegistryKey],
    };
  },
});

/** Typed lookup — throws if entity not found. */
export function getEntityDefinition(entity: EntityRegistryKey): EntityDefinition<BaseEntity> {
  const registry = getEntityRegistry();
  const def = registry[entity];
  if (!def) {
    throw new Error(
      `No entity registry entry for "${entity}". ` +
        `Available: ${ENTITY_REGISTRY_KEYS.join(", ")}`
    );
  }
  return def;
}

/**
 * Resolve a human-friendly display name for an entity record, using the same
 * title-template precedence as the detail modal header
 * (`useDetailModalHeader`): detail config titleTemplate → table config
 * titleTemplate → the record's `title`/`name` → the entity's display label.
 *
 * Use this anywhere a display name is needed for a row record (e.g. the
 * "Ask Meridian" row action) so it never diverges from the detail view.
 */
export function resolveEntityDisplayName(entity: EntityRegistryKey, item: BaseEntity): string {
  const def = getEntityDefinition(entity);
  const record = item as Record<string, unknown>;

  const applyTemplate = (tpl: unknown): string | null => {
    if (typeof tpl === "function") {
      try {
        const result = (tpl as (i: BaseEntity) => string)(item);
        return result && result.trim() ? result : null;
      } catch {
        return null;
      }
    }
    if (typeof tpl === "string") {
      const filled = tpl
        .replace(/\{(\w+)\}/g, (_match, key: string) => {
          const value = record[key];
          return value == null ? "" : String(value);
        })
        .trim();
      return filled || null;
    }
    return null;
  };

  const detailConfig = def.detailConfig as { titleTemplate?: unknown };
  const tableConfig = def.tableConfig as { titleTemplate?: unknown; displayName?: string };

  const fromDetail = applyTemplate(detailConfig?.titleTemplate);
  if (fromDetail) return fromDetail;

  const fromTable = applyTemplate(tableConfig?.titleTemplate);
  if (fromTable) return fromTable;

  if (typeof record.title === "string" && record.title.trim()) return record.title;
  if (typeof record.name === "string" && record.name.trim()) return record.name;

  return tableConfig?.displayName ?? entity;
}

export function getEntityTableConfig<T extends BaseEntity = BaseEntity>(
  entity: EntityRegistryKey
): SimpleTableConfig<T> {
  return getEntityDefinition(entity).tableConfig as unknown as SimpleTableConfig<T>;
}

export function getEntityDetailConfig<T extends BaseEntity = BaseEntity>(
  entity: EntityRegistryKey
): DetailPageConfig<T> {
  return getEntityDefinition(entity).detailConfig as unknown as DetailPageConfig<T>;
}
